import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe } from "lucide-react";
import { getShopInfo } from "@/lib/api";
import { setPublicOrigin } from "@/lib/print";

const niceDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
const upcoming = (shop) => {
  const today = new Date().toISOString().slice(0, 10);
  const until = new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10);
  return (shop.closures || []).filter((c) => c.date >= today && c.date <= until).sort((a, b) => a.date.localeCompare(b.date));
};

export const Footer = () => {
  const [shop, setShop] = useState(null);
  useEffect(() => { getShopInfo().then((s) => { setShop(s); setPublicOrigin(s.public_url); }).catch(() => {}); }, []);
  return (
    <footer className="bg-[#0A0A0A] text-[#F5F5F0] grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-4">
            <img src="/brand/logo.jpg" alt="Whangamatā Cycles" className="w-12 h-12 rounded-xl bg-white p-1 object-contain" />
            <span className="font-serif text-2xl font-semibold">{shop?.name || "Whangamatā Cycles"}</span>
          </div>
          <p className="text-[#F5F5F0]/60 max-w-sm leading-relaxed">
            Bike hire and self-guided adventures in Whangamatā. Ride the coast, hire by the hour, or explore at your own pace with stories at every stop.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-6">
          <p className="overline text-[#F5F5F0]/40 mb-4">Find us</p>
          {shop && (
            <ul className="space-y-2.5 text-[#F5F5F0]/85" data-testid="footer-contact">
              <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 mt-1 text-[#93C5FD] shrink-0" /><a href={shop.maps_url} target="_blank" rel="noreferrer" className="hover:underline" data-testid="footer-address">{shop.address}</a></li>
              <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-[#93C5FD] shrink-0" /><a href={`tel:${shop.phone.replace(/\s/g, "")}`} className="hover:underline" data-testid="footer-phone">{shop.phone}</a></li>
              <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-[#93C5FD] shrink-0" /><a href={`mailto:${shop.email}`} className="hover:underline break-all" data-testid="footer-email">{shop.email}</a></li>
              <li className="flex items-center gap-2.5"><Globe className="w-4 h-4 text-[#93C5FD] shrink-0" /><a href={shop.website} target="_blank" rel="noreferrer" className="hover:underline">{shop.website.replace(/^https?:\/\//, "")}</a></li>
            </ul>
          )}
        </div>
        <div className="md:col-span-3">
          <p className="overline text-[#F5F5F0]/40 mb-4">Hours</p>
          {shop && (
            <ul className="space-y-1 text-sm" data-testid="footer-hours">
              {shop.hours.map(([d, h]) => (
                <li key={d} className="flex justify-between gap-4"><span className="text-[#F5F5F0]/50">{d}</span><span className={h.toLowerCase() === "closed" ? "text-[#F5F5F0]/40" : "text-[#F5F5F0]/85"}>{h}</span></li>
              ))}
              {shop.hours_note && <li className="pt-2 text-xs text-[#F5F5F0]/40 leading-snug">{shop.hours_note}</li>}
              {upcoming(shop).length > 0 && (
                <li className="pt-3" data-testid="footer-holiday-hours">
                  <p className="overline text-[#93C5FD]/80 mb-1.5">Holiday hours</p>
                  <ul className="space-y-1">
                    {upcoming(shop).map((c) => (
                      <li key={c.date} className="flex justify-between gap-4"><span className="text-[#F5F5F0]/50">{niceDate(c.date)}{c.label ? ` · ${c.label}` : ""}</span><span className={c.hours.toLowerCase() === "closed" ? "text-[#F5F5F0]/40" : "text-[#F5F5F0]/85"}>{c.hours}</span></li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 overline text-[#F5F5F0]/40">
        <span>© 2026 {shop?.name || "Whangamatā Cycles"} · <a href="/privacy" data-testid="footer-privacy-link" className="underline hover:text-white">Privacy</a></span>
        <Link to="/book" className="hover:text-[#F5F5F0]/80">Book a ride</Link>
        <Link to="/self-guided" className="hover:text-[#F5F5F0]/80">Self-guided</Link>
      </div>
    </footer>
  );
};
